import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  Timestamp,
  where,
  writeBatch,
  setDoc,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "../Firebase/firebaseconfig";
import { registrarAuditoria } from "../helpers/auditoria";
import { metodoPagoPayload } from "../helpers/metodosPago";
import { toJsDate, toLocalISODate } from "../helpers/fechas";

const COL = "cajas_movimientos";
const CORTES_COL = "cajas_cortes";
const CLUSTERS_COL = "cajas_clusters";

const safeMoney = (value) => Math.max(Number(value || 0), 0);

const normalizeDateInput = (value) => {
  if (!value) return new Date();
  const date = new Date(`${value}T12:00:00`);
  return Number.isNaN(date.getTime()) ? new Date() : date;
};

const movimientoFechaMs = (mov = {}) => {
  const explicit = Number(mov.fechaMs || 0);
  if (explicit > 0) return explicit;
  if (typeof mov.fecha === "string" && mov.fecha) {
    const parsed = normalizeDateInput(mov.fecha).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  const timestampDate = toJsDate(mov.fecha);
  if (timestampDate) return timestampDate.getTime();
  return 0;
};

// Devuelve la fecha del movimiento como "yyyy-mm-dd" si es posible.
// Esto evita que en la UI se intente renderizar un Timestamp como string
// (ya pasa por safeRender) y que los sort/filter con localeCompare fallen.
const movimientoFechaYmd = (mov = {}) => {
  if (typeof mov.fecha === "string" && /^\d{4}-\d{2}-\d{2}/.test(mov.fecha)) {
    return mov.fecha.slice(0, 10);
  }
  const ms = movimientoFechaMs(mov);
  if (!ms) return "";
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const normalizeMovimientoCaja = (id, data = {}) => {
  const fechaYmd = movimientoFechaYmd(data);
  return {
    id,
    ...data,
    // Reemplazamos fecha por su forma yyyy-mm-dd si era objeto Timestamp,
    // preservando el string si ya venía bien.
    fecha: fechaYmd || data.fecha,
    fechaMs: movimientoFechaMs(data),
  };
};

const rid = () =>
  Math.random().toString(36).slice(2, 10) + Date.now().toString(36);

// CAJA-003: Guard de corte cerrado.
// Busca cortes con estado="cerrado" del mismo (cajaScope, cajaId) cuyo rango
// (dateFrom..dateTo) cubra la fechaMs del movimiento. Si hay coincidencia,
// lanza para que ninguna escritura caiga dentro de un período ya cerrado.
export async function assertCorteAbierto({ cajaScope, cajaId, fechaMs }) {
  if (!fechaMs) return;
  const fechaIso = toLocalISODate(new Date(fechaMs));
  const cortesQ = query(
    collection(db, CORTES_COL),
    where("cajaScope", "==", cajaScope || "general"),
    where("cajaId", "==", cajaId || "general"),
    where("estado", "==", "cerrado")
  );
  const snap = await getDocs(cortesQ);
  for (const docSnap of snap.docs) {
    const c = docSnap.data() || {};
    const desde = c.dateFrom || "";
    const hasta = c.dateTo || "9999-12-31";
    if ((!desde || fechaIso >= desde) && fechaIso <= hasta) {
      throw new Error(
        `Período cerrado: existe un corte cerrado de ${cajaScope}/${cajaId} (${desde || "inicio"} → ${hasta}). ` +
        `Reabre el corte antes de modificar movimientos en ese rango.`
      );
    }
  }
}

export const moneyCaja = (value) =>
  Number(value || 0).toLocaleString("es-MX", {
    style: "currency",
    currency: "MXN",
    maximumFractionDigits: 0,
  });

export async function getProyectoCaja(proyectoId) {
  const snap = await getDoc(doc(db, "proyectos", proyectoId));
  if (!snap.exists()) throw new Error("Proyecto no encontrado");
  return { id: snap.id, ...snap.data() };
}

// CAJA-006: límite por defecto 500 movimientos.
// Pasar { dateFrom: "YYYY-MM-DD", dateTo: "YYYY-MM-DD" } para filtrar por rango.
// El hook llamador debe mostrar un aviso si length === limitN.
const CAJA_QUERY_LIMIT = 500;

function buildDateRangeConstraints(dateFrom, dateTo) {
  const constraints = [];
  if (dateFrom) {
    const from = new Date(`${dateFrom}T00:00:00`);
    if (!Number.isNaN(from.getTime())) {
      constraints.push(where("fechaMs", ">=", from.getTime()));
    }
  }
  if (dateTo) {
    const to = new Date(`${dateTo}T23:59:59`);
    if (!Number.isNaN(to.getTime())) {
      constraints.push(where("fechaMs", "<=", to.getTime()));
    }
  }
  return constraints;
}

export async function listMovimientosCajaProyecto(proyectoId, { dateFrom, dateTo, limitN = CAJA_QUERY_LIMIT } = {}) {
  const q = query(
    collection(db, COL),
    where("proyectoId", "==", proyectoId),
    ...buildDateRangeConstraints(dateFrom, dateTo),
    orderBy("fechaMs", "desc"),
    limit(limitN)
  );
  const snap = await getDocs(q);
  return snap.docs.map((item) => normalizeMovimientoCaja(item.id, item.data()));
}

export async function listMovimientosCajaGeneral({ dateFrom, dateTo, limitN = CAJA_QUERY_LIMIT } = {}) {
  const q = query(
    collection(db, COL),
    where("cajaScope", "==", "general"),
    ...buildDateRangeConstraints(dateFrom, dateTo),
    orderBy("fechaMs", "desc"),
    limit(limitN)
  );
  const snap = await getDocs(q);
  return snap.docs.map((item) => normalizeMovimientoCaja(item.id, item.data()));
}

export async function listMovimientosCajaTodos({ dateFrom, dateTo, limitN = CAJA_QUERY_LIMIT } = {}) {
  const q = query(
    collection(db, COL),
    ...buildDateRangeConstraints(dateFrom, dateTo),
    orderBy("fechaMs", "desc"),
    limit(limitN)
  );
  const snap = await getDocs(q);
  return snap.docs.map((item) => normalizeMovimientoCaja(item.id, item.data()));
}

export async function listCajaClusters() {
  const snap = await getDocs(collection(db, CLUSTERS_COL));
  return snap.docs
    .map((item) => ({ id: item.id, ...item.data() }))
    .filter((item) => item.activo !== false)
    .sort((a, b) => String(a.nombre || "").localeCompare(String(b.nombre || ""), "es"));
}

export async function saveCajaCluster({
  id = "",
  nombre,
  descripcion = "",
  proyectoIds = [],
  activo = true,
}) {
  const cleanNombre = String(nombre || "").trim();
  if (!cleanNombre) throw new Error("Captura el nombre del cluster");
  const ids = [...new Set((proyectoIds || []).map((item) => String(item || "").trim()).filter(Boolean))];
  if (!ids.length) throw new Error("Selecciona al menos un proyecto");

  const user = getAuth().currentUser;
  const ref = id ? doc(db, CLUSTERS_COL, id) : doc(collection(db, CLUSTERS_COL));
  const payload = {
    nombre: cleanNombre,
    descripcion: String(descripcion || "").trim(),
    proyectoIds: ids,
    activo: !!activo,
    updatedAt: serverTimestamp(),
    updatedByUid: user?.uid || "",
    updatedByEmail: user?.email || "",
    ...(id ? {} : { createdAt: serverTimestamp(), createdByUid: user?.uid || "", createdByEmail: user?.email || "" }),
  };

  await setDoc(ref, payload, { merge: true });
  await registrarAuditoria({
    accion: id ? "cajas.clusters.editar" : "cajas.clusters.crear",
    entidadTipo: CLUSTERS_COL,
    entidadId: ref.id,
    despues: payload,
    detalle: `Cluster de caja ${id ? "actualizado" : "creado"}: ${cleanNombre}.`,
  });
  return ref.id;
}

export async function listMovimientosCajaPorCxp(cxpId) {
  if (!cxpId) return [];
  const q = query(collection(db, COL), where("cxpId", "==", cxpId));
  const snap = await getDocs(q);
  return snap.docs
    .map((item) => normalizeMovimientoCaja(item.id, item.data()))
    .sort((a, b) => Number(b.fechaMs || 0) - Number(a.fechaMs || 0));
}

export async function listMovimientosCajaPorDeuda(deudaId) {
  if (!deudaId) return [];
  const q = query(collection(db, COL), where("deudaId", "==", deudaId));
  const snap = await getDocs(q);
  return snap.docs
    .map((item) => normalizeMovimientoCaja(item.id, item.data()))
    .sort((a, b) => Number(b.fechaMs || 0) - Number(a.fechaMs || 0));
}

export function calcularResumenCaja(movimientos = []) {
  return movimientos.reduce(
    (acc, mov) => {
      if (mov.cancelado === true || mov.activo === false) {
        acc.cancelados += 1;
        return acc;
      }
      const monto = safeMoney(mov.monto);
      if (mov.tipo === "entrada") {
        acc.entradas += monto;
      } else if (mov.tipo === "salida") {
        acc.salidas += monto;
      }
      acc.movimientos += 1;
      acc.saldo = acc.entradas - acc.salidas;
      return acc;
    },
    { entradas: 0, salidas: 0, saldo: 0, movimientos: 0, cancelados: 0 }
  );
}

const corteIdFor = ({ proyectoId, dateFrom = "", dateTo = "" }) =>
  `${String(proyectoId || "").replace(/[^\w-]/g, "_")}__${dateFrom || "inicio"}__${dateTo || "hoy"}`;

const corteCajaIdFor = ({ cajaScope = "proyecto", cajaId = "", dateFrom = "", dateTo = "" }) =>
  `${String(cajaScope || "proyecto").replace(/[^\w-]/g, "_")}__${String(cajaId || "general").replace(/[^\w-]/g, "_")}__${dateFrom || "inicio"}__${dateTo || "hoy"}`;

export async function getCorteCajaProyecto({ proyectoId, dateFrom = "", dateTo = "" }) {
  if (!proyectoId) return null;
  const corteId = corteIdFor({ proyectoId, dateFrom, dateTo });
  const snap = await getDoc(doc(db, CORTES_COL, corteId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function saveCorteCajaProyecto({
  proyectoId,
  proyectoNombre = "",
  dateFrom = "",
  dateTo = "",
  estado = "abierto",
  resumen = {},
  conciliacion = {},
  notas = "",
}) {
  if (!proyectoId) throw new Error("Selecciona un proyecto");
  const user = getAuth().currentUser;
  const corteId = corteIdFor({ proyectoId, dateFrom, dateTo });
  const payload = {
    proyectoId,
    proyectoNombre,
    dateFrom,
    dateTo,
    estado,
    resumen,
    conciliacion,
    notas,
    updatedAt: serverTimestamp(),
    updatedByUid: user?.uid || "",
    updatedByEmail: user?.email || "",
  };

  await setDoc(doc(db, CORTES_COL, corteId), payload, { merge: true });
  await registrarAuditoria({
    accion: "cajas.guardarCorte",
    entidadTipo: CORTES_COL,
    entidadId: corteId,
    despues: payload,
    detalle: `Corte de caja ${estado} para ${proyectoNombre || proyectoId} (${dateFrom || "inicio"} - ${dateTo || "hoy"}).`,
  });
  return corteId;
}

export async function getCorteCaja({ cajaScope = "general", cajaId = "general", dateFrom = "", dateTo = "" }) {
  const corteId = corteCajaIdFor({ cajaScope, cajaId, dateFrom, dateTo });
  const snap = await getDoc(doc(db, CORTES_COL, corteId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function saveCorteCaja({
  cajaScope = "general",
  cajaId = "general",
  cajaNombre = "Caja general",
  dateFrom = "",
  dateTo = "",
  estado = "abierto",
  resumen = {},
  notas = "",
}) {
  const user = getAuth().currentUser;
  const corteId = corteCajaIdFor({ cajaScope, cajaId, dateFrom, dateTo });
  const payload = {
    cajaScope,
    cajaId,
    cajaNombre,
    dateFrom,
    dateTo,
    estado,
    resumen,
    notas,
    updatedAt: serverTimestamp(),
    updatedByUid: user?.uid || "",
    updatedByEmail: user?.email || "",
  };

  await setDoc(doc(db, CORTES_COL, corteId), payload, { merge: true });
  await registrarAuditoria({
    accion: "cajas.guardarCorte",
    entidadTipo: CORTES_COL,
    entidadId: corteId,
    despues: payload,
    detalle: `Corte ${estado} para ${cajaNombre} (${dateFrom || "inicio"} - ${dateTo || "hoy"}).`,
  });
  return corteId;
}

export async function createMovimientoCaja({
  tipo,
  monto,
  concepto,
  conceptoId = "",
  conceptoTipo = "",
  categoria = "",
  fecha,
  proyectoId,
  proyectoNombre = "",
  fraccionamientoId = "",
  fraccionamientoNombre = "",
  obraId = "",
  obraNombre = "",
  propiedadId = "",
  propiedadFolio = "",
  cxpId = "",
  cxpPagoId = "",
  deudaId = "",
  deudaPagoId = "",
  cajaScope = "",
  cajaId = "",
  clusterId = "",
  transferenciaId = "",
  transferenciaInterna = false,
  origenTipo = "",
  origenHistorico = false,
  metodo = "transferencia",
  referencia = "",
  notas = "",
  origen = "manual",
}) {
  const montoSeguro = safeMoney(monto);
  const tipoSeguro = tipo === "salida" ? "salida" : "entrada";
  const scopeSeguro = cajaScope || (proyectoId ? "proyecto" : "general");
  const cajaIdSeguro = cajaId || (scopeSeguro === "general" ? "general" : proyectoId);
  const metodoInfo = metodoPagoPayload(metodo);
  if (scopeSeguro === "proyecto" && !proyectoId) throw new Error("Selecciona un proyecto");
  if (montoSeguro <= 0) throw new Error("El monto debe ser mayor a cero");
  if (!String(concepto || "").trim()) throw new Error("Captura un concepto");

  const user = getAuth().currentUser;
  const fechaDate = normalizeDateInput(fecha);
  await assertCorteAbierto({
    cajaScope: scopeSeguro,
    cajaId: cajaIdSeguro,
    fechaMs: fechaDate.getTime(),
  });
  const payload = {
    tipo: tipoSeguro,
    monto: montoSeguro,
    cajaScope: scopeSeguro,
    cajaId: cajaIdSeguro,
    clusterId,
    transferenciaId,
    transferenciaInterna: !!transferenciaInterna,
    concepto: String(concepto || "").trim(),
    conceptoId,
    conceptoTipo,
    categoria,
    fecha: toLocalISODate(fechaDate),
    fechaMs: fechaDate.getTime(),
    proyectoId,
    proyectoNombre,
    fraccionamientoId,
    fraccionamientoNombre,
    obraId,
    obraNombre,
    propiedadId,
    propiedadFolio,
    cxpId,
    cxpPagoId,
    deudaId,
    deudaPagoId,
    origenTipo,
    origenHistorico: !!origenHistorico,
    ...metodoInfo,
    referencia,
    notas,
    origen,
    activo: true,
    cancelado: false,
    createdAt: serverTimestamp(),
    createdAtMs: Date.now(),
    createdByUid: user?.uid || "",
    createdByEmail: user?.email || "",
  };

  const ref = await addDoc(collection(db, COL), payload);
  await registrarAuditoria({
    accion: "cajas.crearMovimiento",
    entidadTipo: "cajas_movimientos",
    entidadId: ref.id,
    despues: payload,
    detalle: `Movimiento de caja ${tipoSeguro} por ${moneyCaja(montoSeguro)} en ${proyectoNombre || cajaIdSeguro}.`,
  });
  return ref.id;
}

export async function createTransferenciaCaja({
  monto,
  fecha,
  metodo = "transferencia",
  referencia = "",
  notas = "",
  origen = {},
  destino = {},
}) {
  const montoSeguro = safeMoney(monto);
  if (montoSeguro <= 0) throw new Error("El monto debe ser mayor a cero");
  if (!origen?.scope || !destino?.scope) throw new Error("Selecciona caja origen y destino");
  if (origen.scope === destino.scope && origen.id === destino.id) {
    throw new Error("La caja origen y destino deben ser distintas");
  }
  if (origen.scope === "proyecto" && !(origen.proyectoId || origen.id)) {
    throw new Error("Selecciona el proyecto origen");
  }
  if (destino.scope === "proyecto" && !(destino.proyectoId || destino.id)) {
    throw new Error("Selecciona el proyecto destino");
  }

  const user = getAuth().currentUser;
  const fechaDate = normalizeDateInput(fecha);
  const origenCajaId = origen.id || (origen.scope === "general" ? "general" : origen.proyectoId || "");
  const destinoCajaId = destino.id || (destino.scope === "general" ? "general" : destino.proyectoId || "");
  await assertCorteAbierto({ cajaScope: origen.scope, cajaId: origenCajaId, fechaMs: fechaDate.getTime() });
  await assertCorteAbierto({ cajaScope: destino.scope, cajaId: destinoCajaId, fechaMs: fechaDate.getTime() });
  const transferenciaId = `TR-${rid()}`;
  const metodoInfo = metodoPagoPayload(metodo);
  const salidaRef = doc(collection(db, COL));
  const entradaRef = doc(collection(db, COL));

  const base = {
    monto: montoSeguro,
    fecha: toLocalISODate(fechaDate),
    fechaMs: fechaDate.getTime(),
    ...metodoInfo,
    referencia,
    notas,
    transferenciaId,
    transferenciaInterna: true,
    origenTipo: "transferencia_caja",
    origen: "caja.transferencia",
    activo: true,
    cancelado: false,
    createdAt: serverTimestamp(),
    createdAtMs: Date.now(),
    createdByUid: user?.uid || "",
    createdByEmail: user?.email || "",
  };

  const buildContext = (item = {}) => ({
    cajaScope: item.scope,
    cajaId: item.id || (item.scope === "general" ? "general" : item.proyectoId || ""),
    clusterId: item.clusterId || "",
    proyectoId: item.scope === "proyecto" ? item.proyectoId || item.id || "" : "",
    proyectoNombre: item.scope === "proyecto" ? item.nombre || "" : "",
  });

  const salidaPayload = {
    ...base,
    ...buildContext(origen),
    tipo: "salida",
    concepto: `Transferencia a ${destino.nombre || destino.id || "caja destino"}`,
    conceptoTipo: "Transferencia interna",
    categoria: "transferencia",
  };
  const entradaPayload = {
    ...base,
    ...buildContext(destino),
    tipo: "entrada",
    concepto: `Transferencia desde ${origen.nombre || origen.id || "caja origen"}`,
    conceptoTipo: "Transferencia interna",
    categoria: "transferencia",
  };

  const batch = writeBatch(db);
  batch.set(salidaRef, salidaPayload);
  batch.set(entradaRef, entradaPayload);
  await batch.commit();

  await registrarAuditoria({
    accion: "cajas.transferir",
    entidadTipo: "cajas_movimientos",
    entidadId: transferenciaId,
    despues: { salidaId: salidaRef.id, entradaId: entradaRef.id, monto: montoSeguro, origen, destino },
    detalle: `Transferencia interna por ${moneyCaja(montoSeguro)}.`,
  });

  return { transferenciaId, salidaId: salidaRef.id, entradaId: entradaRef.id };
}

export async function createSalidaCajaConCxpPagada({
  monto,
  concepto,
  conceptoId = "",
  conceptoTipo = "",
  categoria = "",
  fecha,
  proyectoId,
  proyectoNombre = "",
  fraccionamientoId = "",
  fraccionamientoNombre = "",
  obraId = "",
  obraNombre = "",
  propiedadId = "",
  propiedadFolio = "",
  proveedorId = "",
  proveedorNombre = "",
  tipoCosto = "administrativo",
  metodoProrrateo = "ninguno",
  capitalizable = false,
  metodo = "transferencia",
  referencia = "",
  notas = "",
}) {
  const montoSeguro = safeMoney(monto);
  if (!proyectoId) throw new Error("Selecciona un proyecto");
  if (montoSeguro <= 0) throw new Error("El monto debe ser mayor a cero");
  if (!String(concepto || "").trim()) throw new Error("Captura un concepto");
  if (!String(proveedorNombre || "").trim()) throw new Error("Captura el proveedor");

  const user = getAuth().currentUser;
  const fechaDate = normalizeDateInput(fecha);
  // ERP-5 (2026-06-17): esta salida crea un movimiento de caja, así que debe
  // respetar el corte cerrado igual que createMovimientoCaja. Antes saltaba
  // assertCorteAbierto → permitía registrar salidas en períodos ya cerrados.
  await assertCorteAbierto({
    cajaScope: proyectoId ? "proyecto" : "general",
    cajaId: proyectoId || "general",
    fechaMs: fechaDate.getTime(),
  });
  const metodoInfo = metodoPagoPayload(metodo);
  const pagoId = rid();
  const cxpRef = doc(collection(db, "cxp_deudas"));
  const cajaRef = doc(collection(db, COL));
  const proveedorIdClean = String(proveedorId || "").trim();
  const proveedorNombreClean = String(proveedorNombre || "").trim();
  const proveedorRef = proveedorIdClean ? null : doc(collection(db, "proveedores"));
  const proveedorIdFinal = proveedorIdClean || proveedorRef.id;
  const nowTs = Timestamp.now();

  const pago = {
    pagoId,
    cantidad: montoSeguro,
    ...metodoInfo,
    fecha: Timestamp.fromDate(fechaDate),
    createdAt: nowTs,
    cobradorId: user?.uid || null,
    cobradorNombre: user?.displayName || user?.email || "-",
    cobradorEmail: user?.email || "",
    referencia,
    notas,
    cajaMovimientoId: cajaRef.id,
  };

  const cxpPayload = {
    proveedorId: proveedorIdFinal,
    proveedorNombre: proveedorNombreClean,
    referencia,
    importe: montoSeguro,
    enganche: 0,
    mensualidad: montoSeguro,
    frecuencia: "mensual",
    diaPago: null,
    notas,
    proyectoId,
    proyectoNombre,
    fraccionamientoId,
    fraccionamientoNombre,
    obraId,
    obraNombre,
    propiedadId,
    propiedadFolio,
    tipoCosto,
    metodoProrrateo,
    capitalizable: !!capitalizable,
    saldoRestante: 0,
    nextDueDate: null,
    creada: nowTs,
    lastPaymentDate: Timestamp.fromDate(fechaDate),
    pagos: [pago],
    activo: false,
    estado: "liquidada",
    origen: "caja.crearCxpPagada",
    cajaMovimientoId: cajaRef.id,
  };

  const cajaPayload = {
    tipo: "salida",
    monto: montoSeguro,
    cajaScope: "proyecto",
    cajaId: proyectoId,
    clusterId: "",
    transferenciaId: "",
    transferenciaInterna: false,
    concepto: String(concepto || "").trim(),
    conceptoId,
    conceptoTipo,
    categoria,
    fecha: toLocalISODate(fechaDate),
    fechaMs: fechaDate.getTime(),
    proyectoId,
    proyectoNombre,
    fraccionamientoId,
    fraccionamientoNombre,
    obraId,
    obraNombre,
    propiedadId,
    propiedadFolio,
    cxpId: cxpRef.id,
    cxpPagoId: pagoId,
    deudaId: "",
    deudaPagoId: "",
    ...metodoInfo,
    referencia,
    notas,
    origen: "caja.crearCxpPagada",
    activo: true,
    cancelado: false,
    createdAt: serverTimestamp(),
    createdAtMs: Date.now(),
    createdByUid: user?.uid || "",
    createdByEmail: user?.email || "",
  };

  const batch = writeBatch(db);
  if (proveedorRef) {
    batch.set(proveedorRef, {
      nombre: proveedorNombreClean,
      nombreLC: proveedorNombreClean.toLowerCase(),
      activo: true,
      origen: "caja.crearCxpPagada",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdByUid: user?.uid || "",
      createdByEmail: user?.email || "",
    });
  }
  batch.set(cxpRef, cxpPayload);
  batch.set(cajaRef, cajaPayload);
  await batch.commit();

  await registrarAuditoria({
    accion: "cajas.crearCxpPagada",
    entidadTipo: "cajas_movimientos",
    entidadId: cajaRef.id,
    despues: { ...cajaPayload, cxpId: cxpRef.id },
    detalle: `Salida de caja con CxP pagada por ${moneyCaja(montoSeguro)} en ${proyectoNombre || proyectoId}.`,
  });

  return { cajaMovimientoId: cajaRef.id, cxpId: cxpRef.id, pagoId };
}

// AUDIT A-1 (cerrado 2026-06-17): cancelarMovimientoCaja ahora propaga la
// cancelacion al doc origen (cxp_deudas / deudas). Si el movimiento estaba
// vinculado a un pago via cxpId+cxpPagoId o deudaId+deudaPagoId, revertimos
// ese pago del array `pagos[]` y restauramos saldoRestante DENTRO de la misma
// transaccion — para que el origen no quede subvaluado de forma permanente.
//
// Alcance del fix:
//   • Solo aplica al leg individual (los movimientos cxp/deuda nunca son
//     transferencia interna).
//   • Idempotente: si el pago ya no esta en el array (ej. doble cancelacion),
//     no hace nada al origen.
//   • Si el pago liquido la deuda/CxP, reactivamos (activo:true, estado:"activa")
//     solo en ese caso — para no pisar estados como "en_devolucion".
//   • nextDueDate NO se recalcula hacia atras (seria fragil); el saldo es lo
//     critico para conciliacion, la fecha de agenda la ajusta el admin si aplica.

export async function cancelarMovimientoCaja(movimientoId, motivo = "") {
  const user = getAuth().currentUser;
  const ref = doc(db, COL, movimientoId);

  // ── CAJA-005: leer estado actual antes de cualquier escritura ──
  const snapPre = await getDoc(ref);
  if (!snapPre.exists()) throw new Error("Movimiento de caja no encontrado");
  const dataPre = snapPre.data();

  if (dataPre.cancelado === true) {
    throw new Error("Este movimiento ya está cancelado.");
  }

  await assertCorteAbierto({
    cajaScope: dataPre.cajaScope || "general",
    cajaId: dataPre.cajaId || "general",
    fechaMs: movimientoFechaMs(dataPre),
  });

  const patch = {
    activo: false,
    cancelado: true,
    canceladoEn: serverTimestamp(),
    canceladoPor: user?.uid || "",
    canceladoPorEmail: user?.email || "",
    motivoCancelacion: motivo || "Cancelacion operativa",
    updatedAt: serverTimestamp(),
  };

  let cancelados = [movimientoId];

  if (dataPre.transferenciaInterna === true && dataPre.transferenciaId) {
    // Transferencia interna: cancelar ambas piernas atómicamente con writeBatch.
    // Primero leemos los IDs de los legs vinculados (fuera del batch está bien
    // porque la guardia de cancelado===true en cada leg es idempotente).
    const linkedSnap = await getDocs(
      query(collection(db, COL), where("transferenciaId", "==", dataPre.transferenciaId))
    );
    const batch = writeBatch(db);
    linkedSnap.forEach((item) => {
      // Solo cancelar legs que aún están activos
      if (item.data().cancelado !== true) {
        batch.update(item.ref, patch);
        cancelados.push(item.id);
      }
    });
    await batch.commit();
    cancelados = [...new Set(cancelados)];
  } else {
    // Leg individual: runTransaction para leer-verificar-escribir atómicamente.
    // AUDIT A-1: si el movimiento esta vinculado a un pago de CxP o deuda,
    // revertimos ese pago en el doc origen dentro de la MISMA transaccion.
    const origenCol = dataPre.cxpId
      ? "cxp_deudas"
      : (dataPre.deudaId ? "deudas" : null);
    const origenId = dataPre.cxpId || dataPre.deudaId || "";
    const origenPagoId = dataPre.cxpPagoId || dataPre.deudaPagoId || "";
    const debePropagar = !!(origenCol && origenId && origenPagoId);

    await runTransaction(db, async (tx) => {
      // ── 1. Todas las lecturas primero (regla de Firestore tx) ──
      const snap = await tx.get(ref);
      if (!snap.exists()) throw new Error("Movimiento de caja no encontrado");
      if (snap.data().cancelado === true) throw new Error("Este movimiento ya está cancelado.");

      let origenRef = null;
      let origenData = null;
      if (debePropagar) {
        origenRef = doc(db, origenCol, origenId);
        const origenSnap = await tx.get(origenRef);
        if (origenSnap.exists()) origenData = origenSnap.data();
      }

      // ── 2. Cancelar el movimiento de caja ──
      tx.update(ref, patch);

      // ── 3. Revertir el pago en el doc origen ──
      if (origenRef && origenData) {
        const pagos = Array.isArray(origenData.pagos) ? origenData.pagos : [];
        const pagoRevertir = pagos.find((p) => p?.pagoId === origenPagoId);
        if (pagoRevertir) {
          const pagosRestantes = pagos.filter((p) => p?.pagoId !== origenPagoId);
          const montoRevertir = Math.abs(Number(pagoRevertir.cantidad) || 0);
          const saldoActual = Number(origenData.saldoRestante || 0);
          const nuevoSaldo = Math.round((saldoActual + montoRevertir) * 100) / 100;

          const origenPatch = {
            pagos: pagosRestantes,
            saldoRestante: nuevoSaldo,
          };
          // Solo reactivar si el pago habia liquidado el doc (saldo era 0).
          // Evita pisar estados como "en_devolucion".
          if (saldoActual <= 0 && nuevoSaldo > 0) {
            origenPatch.activo = true;
            origenPatch.estado = "activa";
          }
          tx.update(origenRef, origenPatch);
        }
      }
    });
  }

  await registrarAuditoria({
    accion: "cajas.cancelarMovimiento",
    entidadTipo: "cajas_movimientos",
    entidadId: movimientoId,
    detalle: dataPre.transferenciaInterna
      ? `Cancelacion de transferencia interna completa: ${motivo || "Cancelacion operativa"}`
      : motivo || "Cancelacion operativa",
    despues: { cancelados, transferenciaId: dataPre.transferenciaId || "" },
  });
}
